import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Home, Compass, Users, Layers, Search, Bell, Mail, Settings, LogOut, Plus,
  Sparkles, HelpCircle,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NexusLogo from "@/components/NexusLogo";

const NAV = [
  { l: "Início",      to: "/app", icon: Home, exact: true },
  { l: "Explorar",    to: "/app/explorar", icon: Compass },
  { l: "Comunidades", to: "/app/comunidades", icon: Users },
  { l: "Categorias",  to: "/app/categorias", icon: Layers },
];

function ServerRail({ servers, onCreate }) {
  return (
    <aside className="w-[72px] shrink-0 bg-[hsl(var(--nx-panel))] border-r border-[hsl(var(--nx-border))] flex flex-col items-center py-4 gap-2 overflow-y-auto scrollbar-thin" data-testid="server-rail">
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink to="/app" end className={({isActive}) =>
              `w-12 h-12 rounded-2xl grid place-items-center transition-all
              ${isActive ? "nx-btn-blue rounded-[14px]" : "bg-[hsl(var(--nx-panel-3))] hover:rounded-[14px] hover:nx-btn-blue"}`
            }>
              <Home className="w-5 h-5" />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right">Início</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div className="w-8 h-px bg-[hsl(var(--nx-border))] my-1" />

      {servers.map((s) => (
        <TooltipProvider key={s.id} delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink
                to={`/app/servidor/${s.id}`}
                data-testid={`server-icon-${s.id}`}
                className={({ isActive }) =>
                  `relative block w-12 h-12 rounded-2xl overflow-hidden ring-2 transition-all
                  ${isActive ? "ring-[hsl(var(--nx-blue))] rounded-[14px] nx-glow-blue" : "ring-transparent hover:ring-[hsl(var(--nx-blue))]/60 hover:rounded-[14px]"}`
                }
              >
                {s.icon_url ? (
                  <img src={s.icon_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] grid place-items-center font-display font-bold text-white">
                    {s.name?.[0]?.toUpperCase() || "N"}
                  </div>
                )}
              </NavLink>
            </TooltipTrigger>
            <TooltipContent side="right">{s.name}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}

      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onCreate}
              data-testid="server-create-btn"
              className="w-12 h-12 rounded-2xl bg-[hsl(var(--nx-panel-3))] hover:bg-[hsl(var(--nx-green))]/90 hover:text-white hover:rounded-[14px] grid place-items-center transition-all text-[hsl(var(--nx-green))] hover:text-white"
            >
              <Plus className="w-5 h-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Criar comunidade</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavLink to="/app/explorar" className="w-12 h-12 rounded-2xl bg-[hsl(var(--nx-panel-3))] grid place-items-center hover:bg-[hsl(var(--nx-cyan))]/90 hover:text-white hover:rounded-[14px] transition-all text-[hsl(var(--nx-cyan))] hover:text-white">
              <Compass className="w-5 h-5" />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right">Explorar</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </aside>
  );
}

function TopBar({ user, onLogout }) {
  const nav = useNavigate();
  return (
    <header className="h-16 shrink-0 border-b border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel))]/70 backdrop-blur px-4 lg:px-6 flex items-center gap-4 z-30 relative">
      <NexusLogo to="/app" size={30} />
      <div className="hidden lg:block h-6 w-px bg-[hsl(var(--nx-border))]" />

      <nav className="hidden lg:flex items-center gap-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.exact}
            data-testid={`nav-${n.l.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"")}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-colors
              ${isActive ? "text-foreground bg-[hsl(var(--nx-panel-3))]" : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--nx-panel-3))]/60"}`
            }
          >
            <n.icon className="w-4 h-4" /> {n.l}
          </NavLink>
        ))}
      </nav>

      <div className="flex-1 max-w-lg mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            placeholder="Buscar mensagens, canais ou membros…"
            data-testid="global-search"
            className="w-full h-10 pl-10 pr-16 bg-[hsl(var(--nx-panel-2))] border border-[hsl(var(--nx-border))] rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--nx-blue))]"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 border border-[hsl(var(--nx-border))] rounded">Ctrl K</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button className="w-9 h-9 rounded-lg bg-[hsl(var(--nx-panel-2))] hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center transition-colors" data-testid="topbar-notifications" title="Notificações">
          <Bell className="w-4 h-4" />
        </button>
        <button className="w-9 h-9 rounded-lg bg-[hsl(var(--nx-panel-2))] hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center transition-colors" data-testid="topbar-messages" title="Mensagens">
          <Mail className="w-4 h-4" />
        </button>
        <button className="hidden sm:grid w-9 h-9 rounded-lg bg-[hsl(var(--nx-panel-2))] hover:bg-[hsl(var(--nx-panel-3))] place-items-center transition-colors" data-testid="topbar-help" title="Ajuda">
          <HelpCircle className="w-4 h-4" />
        </button>

        <div className="h-6 w-px bg-[hsl(var(--nx-border))] mx-1" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full hover:bg-[hsl(var(--nx-panel-3))] transition-colors">
              <Avatar className="w-8 h-8 ring-2 ring-[hsl(var(--nx-border))]">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="text-xs font-semibold bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] text-white">
                  {user?.username?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden xl:block text-left">
                <div className="text-xs font-semibold leading-none">{user?.display_name || user?.username}</div>
                <div className="text-[10px] text-muted-foreground leading-none mt-1">@{user?.username}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 nx-panel">
            <div className="px-2 py-2">
              <div className="text-sm font-semibold">{user?.display_name || user?.username}</div>
              <div className="text-xs text-muted-foreground">@{user?.username}</div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav(`/app/criador/${user?.username}`)} data-testid="menu-profile">
              <Sparkles className="w-4 h-4 mr-2" /> Meu perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/app/configuracoes")} data-testid="menu-settings">
              <Settings className="w-4 h-4 mr-2" /> Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="text-[hsl(var(--nx-red))] focus:text-[hsl(var(--nx-red))]">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [servers, setServers] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSrv, setNewSrv] = useState({ name: "", description: "" });

  const loadServers = async () => {
    try {
      const { data } = await api.get("/servers/mine");
      setServers(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadServers(); }, []);

  const createServer = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/servers", newSrv);
      setServers((s) => [...s, data]);
      setOpenCreate(false);
      setNewSrv({ name: "", description: "" });
      toast.success(`Comunidade "${data.name}" criada`);
      nav(`/app/servidor/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao criar comunidade");
    } finally {
      setCreating(false);
    }
  };

  const doLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <TopBar user={user} onLogout={doLogout} />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        <ServerRail servers={servers} onCreate={() => setOpenCreate(true)} />

        <div className="flex-1 min-w-0 flex overflow-hidden">
          <Outlet context={{ servers, reloadServers: loadServers }} />
        </div>
      </div>

      {/* Create server dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="nx-panel">
          <DialogHeader>
            <DialogTitle className="font-display">Criar uma comunidade</DialogTitle>
          </DialogHeader>
          <form onSubmit={createServer} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="srv-name">Nome</Label>
              <Input id="srv-name" required minLength={2} maxLength={80}
                value={newSrv.name}
                onChange={(e) => setNewSrv({ ...newSrv, name: e.target.value })}
                data-testid="server-create-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="srv-desc">Descrição</Label>
              <Textarea id="srv-desc" maxLength={280}
                value={newSrv.description}
                onChange={(e) => setNewSrv({ ...newSrv, description: e.target.value })}
                data-testid="server-create-desc" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={creating}
                className="rounded-xl nx-btn-blue border-0 h-11 px-6 font-semibold"
                data-testid="server-create-submit">
                {creating ? "criando…" : "Criar comunidade"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
